import Header from "common/components/Header/Header";
import FilteredList from "common/components/Table/FilteredList";
import useAuth from "common/hooks/useAuth";

import useList from "common/hooks/useList";
import React, {
  // eslint-disable-next-line comma-dangle
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";

import { Sector } from "types/Sector";
import AlertsContext from "common/providers/alerts";
import usePark from "common/hooks/usePark";
import { BarsArrowDownIcon, ListBulletIcon } from "@heroicons/react/24/outline";
import { BsStar, BsStarHalf } from "react-icons/bs";
import RadioBlock from "common/components/RadioBlock/RadioBlock";
import TabBlockItem from "common/components/TabBlockItem/TabBlockItem";
import UserSelect from "components/select/UserSelect/UserSelect";
import HistoryEventTypeSelect from "components/select/History/HistoryEventTypeSelect/HistoryEventTypeSelect";
import HistoryEventModelSelect from "components/select/History/HistoryEventModelSelect/HistoryEventModelSelect";
import DatePicker from "common/components/DatePicker/DatePicker";
import HistoryItem from "./HistoryItem";

function History() {
  const { t } = useTranslation();
  const { setAlert } = useContext(AlertsContext);

  const { park } = usePark();

  const { user: currentUser } = useAuth();

  const {
    items,
    isFetching,
    filters,
    setFilter,
    pageCount,
    setPageIndex,
    pageIndex,
    itemsCount,
    itemsPerPage,
    error,
  } = useList<Sector>("history-events", {
    defaultFilters:
      currentUser?.isSuperadmin && park?.id
        ? {
            park: park.id,
          }
        : {},
    cachePrefix: "History_all",
  });

  useEffect(() => {
    if (error) {
      setAlert(error, "danger");
    }
  }, [error, setAlert]);

  const statusList = useMemo(() => {
    return [
      {
        id: "",
        name: t("status.all"),
        count: null,
        icon: ListBulletIcon,
        color: "secondary",
      },
      {
        id: "backoffice",
        name: t("common.backoffice"),
        count: null,
        icon: BsStarHalf,
        color: "#FFC5A5",
      },
      {
        id: "front",
        name: t("common.front"),
        count: null,
        icon: BsStar,
        color: "#FF9153",
      },
    ];
  }, [t]);

  const styleTop = "md:w-1/3 xl:flex-1 xl:w-auto";

  const columns = useMemo(
    () => [
      {
        Header: t("history.modelNameFilter"),
        filterName: "modelName",
        filterComponent: (props: any) => (
          <HistoryEventModelSelect isFilter {...props} />
        ),
        columnClasses: styleTop,
      },
      {
        Header: t("history.requestType"),
        filterName: "requestType",
        filterComponent: (props: any) => (
          <HistoryEventTypeSelect isFilter {...props} />
        ),
        columnClasses: styleTop,
      },
      {
        Header: t("forms.user"),
        filterName: "author",
        filterComponent: (props: any) => <UserSelect isFilter {...props} />,
        columnClasses: styleTop,
      },
      {
        Header: t("forms.date"),
        filterName: "createdAt",
        columnClasses: "md:flex-1",
        filterComponent: (props: any) => (
          <DatePicker showTimeSelect={false} {...props} />
        ),
      },
    ],
    [t]
  );

  return (
    <div className="mx-auto max-w-full py-4 px-4 text-slate-500">
      <Header
        title={t("titlePages.history")}
        subtitle={t("titleLegends.history")}
        Icon={BarsArrowDownIcon}
        filters={filters}
        setFilter={setFilter}
        isBeforeFilters
        filterName="environment"
        FilterComponent={(props: any) => (
          <RadioBlock
            Component={TabBlockItem}
            items={statusList}
            horizontal
            noGap
            {...props}
          />
        )}
      />

      <FilteredList
        data={items ?? []}
        columns={columns} // used as filter items
        isFetching={isFetching}
        pageCount={pageCount}
        filters={filters}
        setFilter={setFilter}
        setPageIndex={setPageIndex}
        pageIndex={pageIndex}
        RenderItem={HistoryItem}
        itemsCount={itemsCount}
        itemsPerPage={itemsPerPage}
      />
    </div>
  );
}

export default History;
